/**
 * Career & side-hustle event bank.
 * Only surfaced AFTER the learner has passed and activated Career Path
 * (i.e., `learner_career_state.activated_at IS NOT NULL`).
 *
 * Each template can optionally restrict by saved career_key / side_hustle_key.
 * If the restriction arrays are omitted, the event applies to any career/hustle.
 */

import type { EventTemplate } from "@/hooks/useEventEngine";

export type CareerEventCategory = "job_event" | "career_event" | "side_hustle_event";

export interface CareerEventTemplate extends EventTemplate {
  category: CareerEventCategory;
  requiredCareer?: string[];
  requiredHustle?: string[];
}

/* ─── Job events (apply to any active career) ─── */

export const JOB_EVENTS: CareerEventTemplate[] = [
  {
    key: "overtime_offer",
    type: "life_event",
    category: "job_event",
    title: "Overtime Opportunity ⏰",
    description: "Your boss offers a weekend shift for an extra $250. Will you take it?",
    amount: 250,
    difficulty: "early",
    options: [
      { label: "Take it — bank the extra", accountType: "savings", amount: 250, isDeduction: false, explanation: "Saving overtime pay accelerates your goals without changing your lifestyle." },
      { label: "Take it — invest it", accountType: "investment", amount: 250, isDeduction: false, explanation: "Treating overtime as 'investment-only' money is a wealth-building habit." },
      { label: "Decline — protect your time", accountType: "checking", amount: 0, isDeduction: false, explanation: "Burnout costs more than overtime pays. Boundaries matter." },
    ],
  },
  {
    key: "paycheck_variance",
    type: "life_event",
    category: "job_event",
    title: "Smaller Paycheck Than Expected 💼",
    description: "Payroll taxes and benefits were higher this period — your check is $180 short of what you planned.",
    amount: 180,
    difficulty: "mid",
    options: [
      { label: "Cover from Checking", accountType: "checking", amount: 180, isDeduction: true, explanation: "Absorbing variance is normal. Build a buffer for paycheck-to-paycheck swings." },
      { label: "Pull from Emergency Fund", accountType: "emergency_fund", amount: 180, isDeduction: true, explanation: "Reserves exist for this. Just restock when the next check arrives." },
      { label: "Cut discretionary spending", accountType: "checking", amount: 0, isDeduction: false, explanation: "Adjusting spend in-month preserves all your reserves." },
    ],
  },
  {
    key: "promotion_raise",
    type: "life_event",
    category: "job_event",
    title: "Promotion & Raise 🚀",
    description: "Your manager offered a promotion with a $400 monthly raise. How do you handle the new income?",
    amount: 400,
    difficulty: "mid",
    options: [
      { label: "Lifestyle creep — into Checking", accountType: "checking", amount: 400, isDeduction: false, explanation: "Inflating your lifestyle with every raise prevents wealth from compounding." },
      { label: "Save the entire raise", accountType: "savings", amount: 400, isDeduction: false, explanation: "Banking raises is the #1 wealth habit. You won't miss what you never spent." },
      { label: "Boost retirement", accountType: "roth_ira", amount: 400, isDeduction: false, explanation: "Channeling raises into retirement maximizes tax-advantaged compounding." },
    ],
  },
  {
    key: "layoff_scare",
    type: "life_event",
    category: "job_event",
    title: "Layoff Rumors 😬",
    description: "Restructuring rumors are spreading at work. Nothing's confirmed, but it's a wake-up call.",
    amount: 0,
    difficulty: "advanced",
    options: [
      { label: "Aggressively grow Emergency Fund", accountType: "emergency_fund", amount: 300, isDeduction: false, explanation: "3–6 months of expenses is the standard. Build it before you need it." },
      { label: "Update resume & network", accountType: "checking", amount: 0, isDeduction: false, explanation: "The best time to job hunt is when you don't need to. Optionality is power." },
      { label: "Ignore — it's just rumors", accountType: "checking", amount: 0, isDeduction: false, explanation: "Complacency is risky. Even rumors are a signal to prepare." },
    ],
  },
  {
    key: "commission_spike",
    type: "life_event",
    category: "job_event",
    title: "Big Commission Month 📈",
    description: "You crushed quota and earned a $600 commission spike on top of base pay!",
    amount: 600,
    difficulty: "mid",
    options: [
      { label: "Celebrate — into Checking", accountType: "checking", amount: 600, isDeduction: false, explanation: "Enjoying wins is fine, but variable income is best treated as savings/investment fuel." },
      { label: "Invest the windfall", accountType: "investment", amount: 600, isDeduction: false, explanation: "Treating commission as bonus money compounds aggressively over time." },
      { label: "Tax reserve", accountType: "savings", amount: 600, isDeduction: false, explanation: "Variable income comes with variable taxes. Reserving for tax season prevents nasty surprises." },
    ],
  },
  {
    key: "certification_cost",
    type: "life_event",
    category: "job_event",
    title: "Certification Opportunity 🎓",
    description: "A career-boosting certification costs $350. ROI could be significant, but it's real money now.",
    amount: 350,
    difficulty: "mid",
    options: [
      { label: "Invest in yourself — Checking", accountType: "checking", amount: 350, isDeduction: true, explanation: "Skill investments often pay back many times over. Worth the upfront cost." },
      { label: "Use Savings", accountType: "savings", amount: 350, isDeduction: true, explanation: "Strategic use of savings for career growth is legitimate spending." },
      { label: "Skip it for now", accountType: "checking", amount: 0, isDeduction: false, explanation: "Not every certification is worth it. Validate ROI before committing." },
    ],
  },
];

/* ─── Side-hustle events (only surfaced if learner has a saved side hustle) ─── */

export const SIDE_HUSTLE_EVENTS: CareerEventTemplate[] = [
  {
    key: "client_drought",
    type: "entrepreneurship_event",
    category: "side_hustle_event",
    title: "Client Drought 🌵",
    description: "Your side hustle pipeline went quiet. No new clients for 3 weeks. How do you respond?",
    amount: 0,
    difficulty: "mid",
    options: [
      { label: "Invest in outreach ($150)", accountType: "checking", amount: 150, isDeduction: true, explanation: "Slow periods are when marketing matters most. Don't disappear when revenue dips." },
      { label: "Improve portfolio/product", accountType: "checking", amount: 75, isDeduction: true, explanation: "Use the quiet to sharpen your offer. You'll convert better when leads return." },
      { label: "Wait it out", accountType: "checking", amount: 0, isDeduction: false, explanation: "Hoping won't bring clients back. Inaction is a choice." },
    ],
  },
  {
    key: "follower_surge",
    type: "entrepreneurship_event",
    category: "side_hustle_event",
    title: "Follower Surge 📣",
    description: "A post took off. Your audience grew 3x overnight. Engagement is hot.",
    amount: 0,
    difficulty: "early",
    options: [
      { label: "Launch a paid offer", accountType: "checking", amount: 200, isDeduction: false, explanation: "Hot audiences are buying audiences. Strike while attention is on you." },
      { label: "Capture emails for later", accountType: "checking", amount: 0, isDeduction: false, explanation: "Owned audience > rented audience. Email is the most durable channel." },
      { label: "Enjoy the moment", accountType: "checking", amount: 0, isDeduction: false, explanation: "Viral moments fade fast. Monetization windows close." },
    ],
  },
  {
    key: "equipment_upgrade",
    type: "entrepreneurship_event",
    category: "side_hustle_event",
    title: "Equipment Upgrade 🛠️",
    description: "A better tool/camera/laptop would 2x your output quality. It costs $450.",
    amount: 450,
    difficulty: "mid",
    options: [
      { label: "Buy now from Checking", accountType: "checking", amount: 450, isDeduction: true, explanation: "Equipment that pays for itself in months is a great use of cash." },
      { label: "Save up first", accountType: "savings", amount: 450, isDeduction: false, explanation: "Building a dedicated reinvestment fund prevents draining operating cash." },
      { label: "Make do with what you have", accountType: "checking", amount: 0, isDeduction: false, explanation: "Hustlers over-invest in gear they don't need. Sometimes good enough is best." },
    ],
  },
  {
    key: "monetization_unlock",
    type: "entrepreneurship_event",
    category: "side_hustle_event",
    title: "Monetization Unlocked 💰",
    description: "Your hustle just hit the metrics required for a new revenue stream worth ~$300/mo.",
    amount: 300,
    difficulty: "mid",
    options: [
      { label: "Reinvest into growth", accountType: "investment", amount: 300, isDeduction: false, explanation: "Reinvesting new revenue compounds the hustle faster than taking it as income." },
      { label: "Pay yourself", accountType: "checking", amount: 300, isDeduction: false, explanation: "Founders need to get paid too. Sustainable hustles fund real life." },
      { label: "Build a reserve", accountType: "emergency_fund", amount: 300, isDeduction: false, explanation: "Variable income needs variable buffers. Reserves smooth lean months." },
    ],
  },
  {
    key: "carrying_cost_spike",
    type: "entrepreneurship_event",
    category: "side_hustle_event",
    title: "Carrying Costs Spike 📊",
    description: "Subscriptions, hosting, and tools quietly added up. Your monthly hustle overhead jumped $120.",
    amount: 120,
    difficulty: "mid",
    options: [
      { label: "Audit & cut tools", accountType: "checking", amount: 0, isDeduction: false, explanation: "Most hustlers pay for 2x what they actually use. Audit ruthlessly." },
      { label: "Absorb the cost", accountType: "checking", amount: 120, isDeduction: true, explanation: "Sometimes the tools earn it back. Track ROI before cutting blindly." },
      { label: "Raise prices to cover it", accountType: "checking", amount: 0, isDeduction: false, explanation: "Cost increases are a fair reason to revisit pricing. Don't eat margin." },
    ],
  },
];

export const ALL_CAREER_EVENTS: CareerEventTemplate[] = [...JOB_EVENTS, ...SIDE_HUSTLE_EVENTS];

/**
 * Filter career events that match a learner's saved career/hustle.
 * Side-hustle events require a non-null side_hustle_key.
 */
export function eligibleCareerEvents(
  careerKey: string | null | undefined,
  sideHustleKey: string | null | undefined,
): CareerEventTemplate[] {
  return ALL_CAREER_EVENTS.filter((tpl) => {
    if (tpl.category === "side_hustle_event" && !sideHustleKey) return false;
    if (tpl.requiredCareer && careerKey && !tpl.requiredCareer.includes(careerKey)) return false;
    if (tpl.requiredHustle && sideHustleKey && !tpl.requiredHustle.includes(sideHustleKey)) return false;
    return true;
  });
}